/**
 * Presentation helpers for Brand Lift Survey UI.
 * Status labels follow BLS-51 copy: completed is shown as Ended.
 */

import { BLS_MODE, BLS_STATUS, durationToSeconds } from './constants.js';
import { getMetricById } from './metrics.js';
import { regularAudiencePercent } from './validation.js';

export const STATUS_LABELS = {
  [BLS_STATUS.DRAFT]: 'Draft',
  [BLS_STATUS.ACTIVE]: 'Active',
  [BLS_STATUS.PAUSED]: 'Paused',
  [BLS_STATUS.COMPLETED]: 'Ended',
};

export const STATUS_BADGE_COLOR = {
  [BLS_STATUS.DRAFT]: 'secondary',
  [BLS_STATUS.ACTIVE]: 'success',
  [BLS_STATUS.PAUSED]: 'warning',
  [BLS_STATUS.COMPLETED]: 'secondary',
};

export function statusLabel(status) {
  return STATUS_LABELS[status] || status || 'Unknown';
}

export function statusBadgeColor(status) {
  return STATUS_BADGE_COLOR[status] || 'secondary';
}

export function modeLabel(mode) {
  return mode === BLS_MODE.OPTIMIZED ? 'Optimized' : 'Standard';
}

export function metricLabel(metricId) {
  return getMetricById(metricId)?.label || metricId || '—';
}

export function formatUnix(value) {
  if (!value) return '—';
  const ms = Number(value) > 1e12 ? Number(value) : Number(value) * 1000;
  if (!Number.isFinite(ms) || ms <= 0) return '—';
  return new Date(ms).toLocaleString();
}

export function formatDuration(value, unit) {
  if (value == null || value === '') return '—';
  return `${value} ${unit || 'days'}`;
}

export function formatPercent(value, digits = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(digits)}%`;
}

export function audienceSummary(controlSplit) {
  const split = Number(controlSplit) || 0;
  return {
    control: split,
    exposed: split,
    regular: regularAudiencePercent(split),
  };
}

export function reportingStatusCopy(status) {
  if (status === BLS_STATUS.ACTIVE) return 'Active collection';
  if (status === BLS_STATUS.PAUSED) return 'Paused — historical results available';
  if (status === BLS_STATUS.COMPLETED) return 'Ended collection';
  return 'Not collecting';
}

export function downloadSetupSummary(config, selectedSets = []) {
  const payload = {
    exportedAt: new Date().toISOString(),
    survey: {
      name: config.name,
      brand: config.brand,
      status: config.status,
      mode: modeLabel(config.blsMode),
      metric: metricLabel(config.brandMetric),
      question: config.questionWording,
      answers: config.answerOptions,
      controlSplit: config.controlSplit,
      exposureFrequency: config.exposureFrequency,
      askLimit: config.askLimit,
      returnFrequency: config.returnFrequency,
      qualifyingPeriod: {
        min: formatDuration(config.displayWindowMin, config.displayWindowMinUnit),
        max: formatDuration(config.displayWindowMax, config.displayWindowMaxUnit),
      },
      creativeGroups: selectedSets.map((set) => ({
        id: set.id,
        name: set.name,
        folder: set.parentId,
        mediaType: set.mediaType,
      })),
      frequencyTesting: config.frequencyTesting,
      optimization: {
        minPerformanceLead: config.minPerformanceLead,
        minTestPeriod: formatDuration(config.minTestPeriod, config.minTestPeriodUnit),
        minTestVolume: config.minTestVolume,
        startOptimizationWhen: config.startOptimizationWhen,
        flightPromotion: config.flightPromotion,
        performersToPromote: config.performersToPromote,
        winningInventoryPercent: config.winningInventoryPercent,
      },
    },
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${(config.name || 'brand-lift-survey').replace(/\s+/g, '-').toLowerCase()}-setup.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function asNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function pickLatestSurvey(list) {
  if (!Array.isArray(list) || !list.length) return null;
  const sorted = [...list].sort((a, b) => {
    const delta = surveySortTime(b) - surveySortTime(a);
    if (delta) return delta;
    return String(b.id || '').localeCompare(String(a.id || ''));
  });
  const live = sorted.find((item) => item.status === BLS_STATUS.ACTIVE || item.status === BLS_STATUS.PAUSED);
  if (live) return live;
  const ended = sorted.find((item) => item.status === BLS_STATUS.COMPLETED);
  if (ended) return ended;
  return sorted[0] || null;
}

function surveySortTime(item) {
  return asNumber(item?.createdAt, 0) || asNumber(item?.startDate, 0);
}

export function formatPp(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)} pp`;
}

export function formatRelativeLift(value, digits = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}% relative`;
}

export function ratePercent(positive, total) {
  const den = asNumber(total);
  if (den <= 0) return null;
  return (asNumber(positive) / den) * 100;
}

function coerceDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'object' && value.date) return coerceDate(value.date);
  if (typeof value === 'number') {
    const ms = value > 1e12 ? value : value * 1000;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function campaignDateBounds(campaign) {
  return {
    start: coerceDate(campaign?.data?.start) || coerceDate(campaign?.start),
    end: coerceDate(campaign?.data?.end) || coerceDate(campaign?.end),
  };
}

export function parseGmtOffset(value) {
  if (value == null || value === '') return null;
  const match = String(value).trim().match(/GMT\s*([+-])(\d{2}):(\d{2})/)
    || String(value).trim().match(/^([+-])(\d{2}):(\d{2})$/);
  if (!match) return null;
  const minutes = (match[1] === '-' ? -1 : 1) * (Number(match[2]) * 60 + Number(match[3]));
  if (!Number.isFinite(minutes) || Math.abs(minutes) > 14 * 60) return null;
  return minutes;
}

export function formatGmtOffset(minutes) {
  const sign = minutes < 0 ? '-' : '+';
  const abs = Math.abs(minutes);
  const hours = String(Math.floor(abs / 60)).padStart(2, '0');
  const mins = String(abs % 60).padStart(2, '0');
  return `GMT${sign}${hours}:${mins}`;
}

export function reportingGmtOffset(queryTz, timezone) {
  const fromQuery = parseGmtOffset(queryTz);
  if (fromQuery != null) return fromQuery;
  if (typeof timezone === 'string') {
    const parsed = parseGmtOffset(timezone);
    if (parsed != null) return parsed;
  }
  const fromOffset = timezone && parseGmtOffset(timezone.offset);
  if (fromOffset != null) return fromOffset;
  const fromLabel = timezone && parseGmtOffset(timezone.label);
  if (fromLabel != null) return fromLabel;
  return 0;
}

export function zonedParts(value, offsetMinutes = 0) {
  const date = coerceDate(value);
  if (!date) return null;
  const shifted = new Date(date.getTime() + offsetMinutes * 60000);
  const month = shifted.toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' }).replace(/\.$/, '');
  return {
    day: shifted.getUTCDate(),
    month,
    year: shifted.getUTCFullYear(),
    hours: shifted.getUTCHours(),
    minutes: shifted.getUTCMinutes(),
    dayKey: shifted.toISOString().slice(0, 10),
  };
}

function formatZonedShortDate(date, offsetMinutes) {
  const shifted = new Date(date.getTime() + offsetMinutes * 60000);
  return shifted.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: '2-digit',
    timeZone: 'UTC',
  });
}

export function formatShortDate(value, offsetMinutes) {
  const date = coerceDate(value);
  if (!date) return '—';
  if (offsetMinutes == null) {
    return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' });
  }
  return formatZonedShortDate(date, offsetMinutes);
}

export function formatHourlyLiftLabel(value, offsetMinutes) {
  const date = coerceDate(value);
  if (!date) return '';
  if (offsetMinutes == null) {
    const month = date.toLocaleDateString(undefined, { month: 'short' }).replace(/\.$/, '');
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');
    return `${date.getDate()}. ${month}, ${hh}:${mm}`;
  }
  const parts = zonedParts(date, offsetMinutes);
  const hh = String(parts.hours).padStart(2, '0');
  const mm = String(parts.minutes).padStart(2, '0');
  return `${parts.day}. ${parts.month}, ${hh}:${mm}`;
}

export function formatStudyPeriod(from, to, grain = 'day', offsetMinutes) {
  const start = coerceDate(from);
  const end = coerceDate(to);
  if (!start && !end) return '—';
  if (grain === 'hour' && start && end) {
    if (offsetMinutes == null) {
      const opts = { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' };
      return `${start.toLocaleString(undefined, opts)} — ${end.toLocaleString(undefined, opts)}`;
    }
    const stamp = (value) => {
      const parts = zonedParts(value, offsetMinutes);
      const hh = String(parts.hours).padStart(2, '0');
      const mm = String(parts.minutes).padStart(2, '0');
      return `${parts.day} ${parts.month}, ${hh}:${mm}`;
    };
    return `${stamp(start)} — ${stamp(end)}`;
  }
  if (start && end) {
    const startLabel = formatShortDate(start, offsetMinutes);
    const endLabel = formatShortDate(end, offsetMinutes);
    if (startLabel === endLabel) return startLabel;
    return `${startLabel} — ${endLabel}`;
  }
  return start ? `From ${formatShortDate(start, offsetMinutes)}` : `Until ${formatShortDate(end, offsetMinutes)}`;
}

export function campaignDateRangeLabel(campaign) {
  const { start, end } = campaignDateBounds(campaign);
  if (!start && !end) return '—';
  if (start && end) {
    const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
    return `${formatShortDate(start)} — ${formatShortDate(end)} — ${days} days total`;
  }
  return start ? `From ${formatShortDate(start)}` : `Until ${formatShortDate(end)}`;
}

export function studyPeriodRemainingLabel(campaign) {
  const { end } = campaignDateBounds(campaign);
  if (!end) return '—';
  const days = Math.ceil((end.getTime() - Date.now()) / 86400000);
  if (days < 0) return 'Campaign ended';
  if (days === 0) return 'Ends today';
  return `${days} day${days === 1 ? '' : 's'} remaining`;
}

export function resultStatusForSurvey({ status }) {
  if (status === BLS_STATUS.PAUSED) {
    return { label: 'Paused', tone: 'paused' };
  }
  if (status === BLS_STATUS.COMPLETED) {
    return { label: 'Ended', tone: 'ended' };
  }
  if (status === BLS_STATUS.DRAFT) {
    return { label: 'Draft', tone: 'collecting' };
  }
  return null;
}

/**
 * Same gates as the optimizer: elapsed test period, impressions per flight,
 * and the Brand Lift gap between the last promoted flight and the next one.
 */
export function optimizationAssessment({ rows, config, survey, volumes, delivery, promotion, periodClock }) {
  const status = survey?.status || config?.status;
  const locked = resultStatusForSurvey({ status });
  const settings = optimizationSettings(config, survey);
  const flights = rankedFlights(rows);
  const comparison = comparePromotionBoundary(flights, settings.promoteCount);
  const period = periodGate(settings.periodSeconds, survey?.startDate, periodClock);
  const volume = volumeGate(volumes, settings.volumeTarget, flights);
  const gatesOpen = settings.requireBoth
    ? period.met && volume.met
    : period.met || volume.met;
  const clears = Boolean(comparison && comparison.leadPct != null && comparison.leadPct + 0.000000001 >= settings.leadThreshold);
  const context = {
    settings,
    flights,
    comparison,
    period,
    volume,
    gatesOpen,
    clears,
    status,
    paused: status === BLS_STATUS.PAUSED,
    ended: status === BLS_STATUS.COMPLETED,
    deliveryLabel: deliveryLabel(delivery, flights),
    promoted: Boolean(promotion),
  };

  let resultStatus = locked;
  if (!resultStatus) {
    if (!gatesOpen) resultStatus = { label: 'Collecting', tone: 'collecting' };
    else if (!comparison || !clears) resultStatus = { label: 'Below minimum', tone: 'collecting' };
    else resultStatus = { label: 'Ready to review', tone: 'ready' };
  }

  return {
    ...settings,
    flights,
    comparison,
    period,
    volume,
    gatesOpen,
    clears,
    promotedNames: comparison ? flights.slice(0, settings.promoteCount).map((flight) => flight.name) : [],
    resultStatus,
    meaning: meaningParagraphs(context),
    recommendation: recommendationParagraphs(context),
  };
}

function optimizationSettings(config, survey) {
  const periodSeconds = survey?.minimumTestPeriod != null
    ? asNumber(survey.minimumTestPeriod)
    : durationToSeconds(config?.minTestPeriod, config?.minTestPeriodUnit);
  const requireBoth = survey?.bothMinimums == null
    ? config?.startOptimizationWhen !== 'either'
    : Boolean(survey.bothMinimums);
  const alwaysOn = survey?.alwaysOn == null
    ? config?.flightPromotion !== 'full'
    : Boolean(survey.alwaysOn);
  return {
    promoteCount: Math.max(1, asNumber(survey?.nPromotions ?? config?.performersToPromote) || 1),
    leadThreshold: asNumber(survey?.minimumPerformanceLead ?? config?.minPerformanceLead),
    volumeTarget: asNumber(survey?.minimumDataSamplePool ?? config?.minTestVolume),
    allocation: asNumber(survey?.topPerformerAllocation ?? config?.winningInventoryPercent) || 80,
    periodSeconds,
    periodLabel: formatPeriodSeconds(periodSeconds),
    requireBoth,
    alwaysOn,
  };
}

function percentagePointLift(value) {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function rankedFlights(rows) {
  return (Array.isArray(rows) ? rows : [])
    .filter((row) => String(row?.flight_id || '').toLowerCase() !== 'all')
    .map((row) => ({
      name: row.flight_id || 'Flight',
      exposedRate: asNumber(row.exposed_positive_response_rate),
      exposedAnswers: asNumber(row.exposed_answers),
      responses: asNumber(row.total_responses),
      ppl: percentagePointLift(row.percentage_point_lift),
    }))
    .sort((a, b) => {
      if (a.ppl == null && b.ppl == null) return String(a.name).localeCompare(String(b.name));
      if (a.ppl == null) return 1;
      if (b.ppl == null) return -1;
      return b.ppl - a.ppl || String(a.name).localeCompare(String(b.name));
    });
}

function comparePromotionBoundary(flights, promoteCount) {
  if (flights.length < 2 || promoteCount < 1 || promoteCount >= flights.length) return null;
  const leader = flights[promoteCount - 1];
  const next = flights[promoteCount];
  const leaderLift = leader.ppl;
  const nextLift = next.ppl;
  if (leaderLift == null || nextLift == null) {
    return { leader, next, leaderLift, nextLift, leadPct: null };
  }
  const leadPct = nextLift === 0
    ? (leaderLift > 0 ? 101 : 0)
    : ((leaderLift - nextLift) / Math.abs(nextLift)) * 100;
  return { leader, next, leaderLift, nextLift, leadPct };
}

function periodGate(periodSeconds, startDate, clock) {
  if (clock && clock.periodFromVolume === true) {
    return volumePeriodGate(periodSeconds, clock);
  }
  if (clock && clock.periodFromVolume === false && clock.periodReadyAt) {
    const start = coerceDate(clock.volumeReachedAt) || coerceDate(startDate);
    return clockPeriodGate(periodSeconds, start, coerceDate(clock.periodReadyAt));
  }
  const start = coerceDate(startDate);
  if (!start) {
    return { met: false, known: true, text: 'No start date is set, so the minimum test period has not started.' };
  }
  const readyAt = new Date(start.getTime() + Math.max(0, periodSeconds) * 1000);
  return clockPeriodGate(periodSeconds, start, readyAt);
}

function volumePeriodGate(periodSeconds, clock) {
  const label = formatPeriodSeconds(periodSeconds);
  const reached = coerceDate(clock.volumeReachedAt);
  const ready = coerceDate(clock.periodReadyAt);
  if (!reached) {
    return {
      met: false,
      known: true,
      text: 'The minimum test period has not started. It begins on the day every flight first reaches 1,000 impressions.',
    };
  }
  const reachedLabel = reached.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const readyLabel = ready
    ? ready.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  if (ready && ready.getTime() <= Date.now()) {
    return {
      met: true,
      known: true,
      text: `The minimum test period of ${label} has passed. It started on ${reachedLabel}, when every flight first reached 1,000 impressions.`,
    };
  }
  return {
    met: false,
    known: true,
    text: `Every flight first reached 1,000 impressions on ${reachedLabel}. The ${label} test period runs from then. The earliest promotion check is ${readyLabel}.`,
  };
}

function clockPeriodGate(periodSeconds, start, readyAt) {
  const label = formatPeriodSeconds(periodSeconds);
  if (!start || !readyAt) {
    return { met: false, known: true, text: 'No start date is set, so the minimum test period has not started.' };
  }
  const started = start.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  const opensOn = readyAt.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  if (readyAt.getTime() <= Date.now()) {
    return {
      met: true,
      known: true,
      text: `The minimum test period of ${label} has passed (the study started ${started}).`,
    };
  }
  return {
    met: false,
    known: true,
    text: `The minimum test period is ${label}, starting ${started}. The earliest promotion check is ${opensOn}.`,
  };
}

function volumeGate(volumes, volumeTarget, flights) {
  if (!volumes || typeof volumes !== 'object' || Array.isArray(volumes)) {
    return { met: true, known: false, text: '' };
  }
  const names = flights.length
    ? flights.map((flight) => flight.name)
    : Object.keys(volumes);
  const short = names
    .map((name) => ({ name, impressions: asNumber(volumes[name]) }))
    .filter((row) => row.impressions < volumeTarget);
  const targetLabel = volumeTarget.toLocaleString();
  if (!short.length) {
    return {
      met: true,
      known: true,
      text: `Every flight is at or above the ${targetLabel} impression minimum test volume.`,
    };
  }
  const detail = short
    .map((row) => {
      const missing = Math.max(0, volumeTarget - row.impressions);
      return `${row.name} has ${row.impressions.toLocaleString()} of ${targetLabel} impressions and needs ${missing.toLocaleString()} more`;
    })
    .join('; ');
  return {
    met: false,
    known: true,
    text: `${detail}.`,
  };
}

function meaningParagraphs(ctx) {
  const { flights, comparison, paused, ended, promoted, deliveryLabel } = ctx;
  if (!flights.length) {
    const frame = paused
      ? 'The survey is paused, and no flight has survey answers yet.'
      : ended
        ? 'The survey has ended without per-flight survey answers, so there is no final ranking.'
        : 'The survey is active, and no flight has survey answers yet.';
    return [
      `${frame} Brand Lift is what ranks a flight, so there is no leader to name.`,
      'Overall Brand Lift on the study can still show a number once any answers arrive. Flight promotion waits until at least two flights have answers.',
    ];
  }

  const paragraphs = [];
  if (paused) {
    paragraphs.push('The survey is paused, so these figures stay where they were when collection stopped. Automatic optimization runs only while the survey is active.');
  } else if (ended) {
    paragraphs.push(promoted
      ? `The survey has ended. Delivery was promoted to ${deliveryLabel}, and that mix is the outcome of this test.`
      : 'The survey has ended, so this is the final read. Automatic optimization will not move delivery again.');
  } else if (promoted) {
    paragraphs.push(`Delivery has already been optimized and is currently ${deliveryLabel}. The latest answers are checked against the same promotion rule.`);
  } else if (!ctx.gatesOpen) {
    paragraphs.push('This is an early read. The order can still change while the test requirements are open, so the current leader is not a result to act on yet.');
  } else if (!comparison) {
    paragraphs.push('The flights have answers and the start rule is open, but there is no flight left outside the promoted set to measure a gap against.');
  } else if (ctx.clears) {
    paragraphs.push(`${comparison.leader.name} has cleared the promotion rule against ${comparison.next.name}. The next optimization run is allowed to shift delivery.`);
  } else if (comparison.leadPct == null) {
    paragraphs.push(`${comparison.leader.name} and ${comparison.next.name} do not both have a Brand Lift figure yet, so the promotion gap is not ready.`);
  } else {
    paragraphs.push(`${comparison.leader.name} is ahead on Brand Lift, and the lead is still inside the minimum you set.`);
  }

  paragraphs.push(comparisonParagraph(ctx));
  paragraphs.push(requirementsParagraph(ctx));
  const sample = sampleParagraph(comparison || { leader: flights[0], next: null });
  if (sample) paragraphs.push(sample);
  const liftNote = exposedRateNote(flights);
  if (liftNote) paragraphs.push(liftNote);
  return paragraphs.filter(Boolean);
}

function recommendationParagraphs(ctx) {
  const { settings, comparison, flights, paused, ended, promoted, gatesOpen, clears, deliveryLabel } = ctx;
  if (!flights.length) {
    if (paused) {
      return ['Resume the survey when you want answers to start coming in. Leave delivery unchanged until two flights have responses to compare.'];
    }
    if (ended) {
      return ['This study is closed. A new Brand Lift Survey would be a separate test; this one has no flight result to promote from.'];
    }
    return [
      `Leave delivery as it is (${deliveryLabel}).`,
      'People still need to see the ads and answer the survey. Shifting budget before that would pick a flight with no result behind it.',
    ];
  }
  if (!comparison) {
    return [
      `The test is set to promote ${settings.promoteCount} flight${settings.promoteCount === 1 ? '' : 's'}, and ${flights.length} flight${flights.length === 1 ? ' is' : 's are'} in the study. Promotion needs one more flight outside that set so there is something to beat.`,
      'Add another flight to the study, or lower Top performers to promote, before a winner can be named. Until then, leave the current mix in place.',
    ];
  }

  const names = flights.slice(0, settings.promoteCount).map((flight) => flight.name);
  const nameList = listNames(names);
  const threshold = formatGap(settings.leadThreshold);

  if (paused) {
    return [
      'Resume the survey if you want more answers and another promotion check. While it stays paused, leave the delivery mix where it is.',
      `A later run will promote only when ${comparison.leader.name} leads ${comparison.next.name} by at least ${threshold}% on Brand Lift.`,
    ];
  }
  if (ended) {
    return [
      promoted
        ? `Nothing further changes on this survey. ${deliveryLabel} is the mix this test settled on.`
        : 'Nothing further changes on this survey. Use the flight rates above as the result, and set the next campaign’s mix from that read.',
      settings.alwaysOn
        ? 'Always-on would have kept measuring after a promotion. This survey is already closed, so that next check will not happen here.'
        : 'Full promotion ends the survey once it runs. With the survey already ended, delivery stays on the mix above.',
    ];
  }
  if (!gatesOpen) {
    return [
      `Hold the current split (${deliveryLabel}). ${waitSentence(ctx)}`,
      'The flight in front today can fall behind as the remaining impressions and answers arrive. Promotion waits until the start rule is met and the Brand Lift gap clears the minimum.',
    ];
  }
  if (!clears) {
    if (comparison.leadPct == null) {
      return [
        `Hold the current split (${deliveryLabel}). ${comparison.leader.name} and ${comparison.next.name} do not both have a Brand Lift figure yet, so promotion waits.`,
        'The next automatic run moves weight only after both flights have a Brand Lift and that gap clears the minimum.',
      ];
    }
    const shortfall = Math.max(0, settings.leadThreshold - comparison.leadPct);
    const close = shortfall > 0 && shortfall <= 3;
    return [
      `Hold the current split (${deliveryLabel}). ${comparison.leader.name} needs to lead ${comparison.next.name} by at least ${threshold}% before delivery should move. The gap is ${formatGap(comparison.leadPct)}% today${close ? ', which is close' : ''}.`,
      promoted
        ? 'An earlier promotion is still in place. The next run leaves that mix alone until a flight clears the minimum again.'
        : 'The next automatic run will move weight only after that gap is cleared, and only while the survey stays active.',
    ];
  }
  if (promoted) {
    return [
      `The latest answers still support the promoted mix (${deliveryLabel}). Leave it in place.`,
      settings.alwaysOn
        ? `Always-on keeps the survey measuring. A later run moves weight only if a different flight clears the same ${threshold}% gap against the flight just below the promoted set.`
        : 'Full promotion already gave the leading flights the inventory and ended further testing on this survey.',
    ];
  }
  if (settings.alwaysOn) {
    const share = settings.promoteCount === 1 ? 'that flight' : 'those flights';
    return [
      `Let the next optimization run give ${nameList} ${settings.allocation}% of impressions between them, and leave ${100 - settings.allocation}% on the other flights.`,
      `That is the always-on rule: ${share} get the top-performer share, and the rest of the inventory stays in the test so Brand Lift can keep updating.`,
    ];
  }
  return [
    `Let the next optimization run give ${nameList} 100% of impressions.`,
    'That is full promotion: the survey ends when the shift happens, so the rates above are the last measurement before delivery moves.',
  ];
}

function comparisonParagraph(ctx) {
  const { settings, flights, comparison } = ctx;
  if (!comparison) {
    const listed = listNames(flights.map((flight) => flight.name));
    return `Answers so far are on ${listed}. Promoting the top ${settings.promoteCount} leaves no remaining flight to compare against, so a winner cannot be named from this setup.`;
  }
  const { leader, next, leaderLift, nextLift, leadPct } = comparison;
  const boundary = settings.promoteCount > 1
    ? ` The test promotes ${settings.promoteCount} flights, so the gap that decides it is between ${leader.name} (last in that set) and ${next.name}, the first flight that would stay behind.`
    : '';
  if (leaderLift == null || nextLift == null || leadPct == null) {
    return `${leader.name} and ${next.name} do not both have a Brand Lift figure yet, so the promotion gap cannot be checked.${boundary}`;
  }
  if (nextLift === 0 && leaderLift > 0) {
    return `${leader.name} Brand Lift is ${formatPp(leaderLift)}. ${next.name} is at 0 pp. With the next flight at 0 pp, this clears the ${formatGap(settings.leadThreshold)}% minimum Brand Lift difference.${boundary}`;
  }
  const ahead = leadPct + 1e-9 >= settings.leadThreshold ? 'above' : 'short of';
  return `${leader.name} Brand Lift is ${formatPp(leaderLift)}. ${next.name} is ${formatPp(nextLift)}. ${leader.name} is ${formatGap(leadPct)}% higher, which is ${ahead} the ${formatGap(settings.leadThreshold)}% minimum Brand Lift difference.${boundary}`;
}

function requirementsParagraph(ctx) {
  const { settings, period, volume, gatesOpen } = ctx;
  const rule = settings.requireBoth
    ? 'Optimization starts when the minimum test period has passed and every flight has reached the impression minimum. The period starts on its own when every flight first reaches 1,000 impressions, and those impressions also count toward the minimum.'
    : 'Optimization starts when either the minimum test period has passed or every flight has reached the impression minimum. The period starts on its own when every flight first reaches 1,000 impressions. It does not wait for the impression minimum.';
  const periodText = period.text;
  const volumeText = volume.known
    ? volume.text
    : 'Impression totals are not on this report yet, so the volume requirement cannot be confirmed from this page.';
  if (gatesOpen && !settings.requireBoth && !period.met && volume.met) {
    return `${rule} ${periodText}`;
  }
  if (gatesOpen && !volume.known) {
    return `${rule} ${periodText} ${volumeText}`;
  }
  if (gatesOpen) {
    return `${rule} ${periodText} ${volumeText} The start rule is satisfied, so the Brand Lift gap is what decides promotion.`;
  }
  return `${rule} ${periodText} ${volumeText}`;
}

function sampleParagraph(comparison) {
  const thin = [comparison.leader, comparison.next].filter((flight) => flight && flight.exposedAnswers > 0 && flight.exposedAnswers < 30);
  if (!thin.length) return '';
  const listed = thin.map((flight) => `${flight.name} (${flight.exposedAnswers.toLocaleString()} exposed answers)`).join(' and ');
  return `${listed} ${thin.length === 1 ? 'is' : 'are'} still a small sample, so a few more responses can change the order.`;
}

function exposedRateNote(flights) {
  if (!flights[0] || flights[0].ppl == null) return '';
  const byRate = [...flights].sort((a, b) => b.exposedRate - a.exposedRate);
  if (!byRate[0] || byRate[0].name === flights[0].name) return '';
  return `${byRate[0].name} has the highest exposed positive response rate. Promotion follows Brand Lift, which is highest on ${flights[0].name} (${formatPp(flights[0].ppl)}).`;
}

function waitSentence(ctx) {
  const { settings, period, volume } = ctx;
  const periodBit = `the ${settings.periodLabel} minimum test period has passed`;
  const volumeBit = `each flight has reached ${settings.volumeTarget.toLocaleString()} impressions`;
  const periodStart = 'The period starts on the day every flight first reaches 1,000 impressions.';
  if (!settings.requireBoth) {
    if (!period.met && volume.known && !volume.met) {
      return `Wait until either ${periodBit} or ${volumeBit}. ${periodStart}`;
    }
    return `Wait until either ${periodBit} or ${volumeBit}. ${periodStart}`;
  }
  if (!period.met && volume.known && !volume.met) {
    return `Wait until every flight first reaches 1,000 impressions and the ${settings.periodLabel} from that day has passed, and until ${volumeBit}.`;
  }
  if (!period.met && (!volume.known || volume.met)) {
    return `Wait until ${periodBit}. ${periodStart}`;
  }
  if (period.met && volume.known && !volume.met) {
    return `The test period has passed. Wait until ${volumeBit}.`;
  }
  return `Wait until both ${periodBit} and ${volumeBit}. ${periodStart}`;
}

function deliveryLabel(delivery, flights) {
  const entries = Object.entries(delivery || {}).filter(([, value]) => value != null && value !== '');
  if (entries.length) {
    return entries.map(([name, value]) => `${name} ${Math.round(Number(value))}%`).join(' · ');
  }
  if (flights.length) {
    const even = Math.round(100 / flights.length);
    return flights.map((flight) => `${flight.name} ${even}%`).join(' · ');
  }
  return 'an even split';
}

function listNames(names) {
  if (names.length <= 1) return names[0] || 'the leading flight';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function formatGap(value) {
  return (Math.round((Number(value) || 0) * 10) / 10).toFixed(1);
}

function formatPeriodSeconds(seconds) {
  const n = Math.max(0, asNumber(seconds));
  if (n > 0 && n % 86400 === 0) {
    const days = n / 86400;
    return `${days} day${days === 1 ? '' : 's'}`;
  }
  if (n > 0 && n % 3600 === 0) {
    const hours = n / 3600;
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  return `${n} seconds`;
}
